import React from "react";
import { Button, Card, Space, Typography } from "antd";
import CopyableText from "components/common/CopyableText";
import SubscriptionDrawerShell from "./SubscriptionDrawerShell";
import {
  formatCurrency,
  formatDateTime,
  formatLabel,
  getStatusTag,
  isFreePlanRecord,
} from "./subscriptionConstants";

const { Text } = Typography;

const FIELD_PRIORITY = [
  "id",
  "name",
  "status",
  "active",
  "subscriberType",
  "subscriberId",
  "subscriptionPlan",
  "subscriptionPlanName",
  "subscriptionType",
  "subscriptionPlanId",
  "subscriptionPaymentType",
  "paymentStatus",
  "amount",
  "currency",
  "paymentId",
  "paymentProcessorId",
  "startDate",
  "endDate",
  "description",
  "createdAt",
  "updatedAt",
];

const isEmptyValue = (value) => value === null || value === undefined || value === "";

const looksLikeDateField = (key) => /date|time|at|on$/i.test(String(key || ""));

const formatFieldValue = (key, value, record = {}) => {
  if (isEmptyValue(value)) return "-";

  if (Array.isArray(value)) {
    if (!value.length) return "-";
    return value.map((item) => formatFieldValue(key, item, record)).join(", ");
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "object") {
    const entries = Object.entries(value).filter(([, nestedValue]) => !isEmptyValue(nestedValue));
    if (!entries.length) return "-";

    return (
      <Space direction="vertical" size={0} style={{ width: "100%" }}>
        {entries.map(([nestedKey, nestedValue]) => (
          <Text key={nestedKey} type="secondary" style={{ wordBreak: "break-word" }}>
            {formatLabel(nestedKey)}: {formatFieldValue(nestedKey, nestedValue, record)}
          </Text>
        ))}
      </Space>
    );
  }

  const normalizedKey = String(key || "").toLowerCase();

  if (looksLikeDateField(normalizedKey)) {
    return formatDateTime(value);
  }

  if (normalizedKey === "status" || normalizedKey.endsWith("status") || normalizedKey === "paymentstatus") {
    return getStatusTag(value);
  }

  if (normalizedKey === "amount") {
    return formatCurrency(value, record?.currency);
  }

  if (normalizedKey.endsWith("id")) {
    return <CopyableText value={value} showTooltip={false} />;
  }

  return String(value);
};

const getOrderedFields = (record = {}) =>
  Object.entries(record)
    .filter(([key]) => {
      const isPlanRecord = Object.prototype.hasOwnProperty.call(record, "subscriptionType");
      if (key === "code" && isPlanRecord) return false;
      if (key === "subscriptionPlanCode") return false;
      if (key === "paymentStatus" && isFreePlanRecord(record)) return false;
      return true;
    })
    .filter(([, value]) => !isEmptyValue(value))
    .sort(([leftKey], [rightKey]) => {
      const leftIndex = FIELD_PRIORITY.indexOf(leftKey);
      const rightIndex = FIELD_PRIORITY.indexOf(rightKey);
      if (leftIndex !== -1 && rightIndex !== -1) return leftIndex - rightIndex;
      if (leftIndex !== -1) return -1;
      if (rightIndex !== -1) return 1;
      return leftKey.localeCompare(rightKey);
    });

const DetailRow = ({ label, children }) => (
  <div
    style={{
      padding: "10px 0",
      borderBottom: "1px solid #f1f5f9",
    }}
  >
    <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600, marginBottom: 6 }}>
      {label}
    </div>
    <div style={{ color: "#0f172a", wordBreak: "break-word" }}>{children}</div>
  </div>
);

export default function SubscriptionRawDrawer({
  open,
  record,
  title,
  onClose,
}) {
  return (
    <SubscriptionDrawerShell
      open={open}
      title={title || "Details"}
      width={860}
      zIndex={1700}
      onClose={onClose}
      bodyStyle={{ overflowY: "auto" }}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      {!record ? (
        <Text type="secondary">No details available.</Text>
      ) : (
        <Card size="small" style={{ borderRadius: 12 }}>
          {getOrderedFields(record).map(([key, value]) => (
            <DetailRow key={key} label={formatLabel(key)}>
              {formatFieldValue(key, value, record)}
            </DetailRow>
          ))}
        </Card>
      )}
    </SubscriptionDrawerShell>
  );
}
